import type { MoveMoveEvent } from "@react-types/shared";
import { type FC, useEffect, useRef, useState } from "react";
import { type MoveEvents, useMove } from "react-aria";
import { useDispatch } from "react-redux";
import { useDebouncedCallback } from "use-debounce";

import { formatNumberByThousands } from "widgets/utils";

import { clamp, getRangeLength } from "../../app-utils/range.js";
import { type Range } from "../../graphql.js";
import { selectRange, updateSelectionModelFromRange } from "../../redux/modules/genome-browser.js";
import { type ReduxDispatch } from "../../redux/redux-utils.js";
import { AXIS_HEIGHT } from "../../settings/settings.js";
import { RangeSelectionMode } from "./GenomeViewerInteractiveSurface.js";
import { useGenomeViewerData } from "./_mod.js";

interface IGenomeViewerSelectionRectangleProps {
  selectionRange: Range;
  selectionMode: RangeSelectionMode;
  modifySelectionRange: (selectionRangeBp: Range) => void;
  viewToBasePosition: (x: number) => number;
  createRangeOnContig: (pos1: number, pos2: number) => Range;
}

const MIN_SELECTION_LABEL_WIDTH = 80;
const SELECTION_LABEL_YPOS = Math.floor(AXIS_HEIGHT / 2) + 3;
const RESIZE_HANDLE_WIDTH = 6;

const GenomeViewerSelectionRectangle: FC<IGenomeViewerSelectionRectangleProps> = (props) => {
  const dispatch = useDispatch<ReduxDispatch>();
  const { layout, normalize } = useGenomeViewerData();

  // We keep the ranges changes local so that the parent doesn't re-render this component on every range changes
  // because it breaks the drag events here. We only update it onDragEnd.
  const [currentSelectionRange, setCurrentSelectionRange] = useState<Range>(props.selectionRange);
  const previousSelectionRangeRef = useRef(props.selectionRange);
  const nextSelectionRangeRef = useRef(props.selectionRange);
  const movingDeltaXRef = useRef(0); // `event.deltaX` is the difference between 2 events, not between start and end.

  const onRectHandleMoveStart = () => {
    previousSelectionRangeRef.current = props.selectionRange;
    movingDeltaXRef.current = 0;
  };

  const onRectHandleMoveEnd = () => {
    // We can't rely on currentSelectionRange as the state update is happening on next tick.
    props.modifySelectionRange(nextSelectionRangeRef.current);
  };

  const onLeftRectHandleMove = (event: MoveMoveEvent) => {
    movingDeltaXRef.current += event.deltaX;
    const start = Math.round(normalize(previousSelectionRangeRef.current.start));
    const newRangeStart = props.viewToBasePosition(start + movingDeltaXRef.current);
    const selectionRangeBp = props.createRangeOnContig(newRangeStart, previousSelectionRangeRef.current.stop);
    nextSelectionRangeRef.current = selectionRangeBp;
    setCurrentSelectionRange(selectionRangeBp);
  };

  const onRightRectHandleMove = (event: MoveMoveEvent) => {
    movingDeltaXRef.current += event.deltaX;
    const stop = Math.round(normalize(previousSelectionRangeRef.current.stop));
    const newRangeStop = props.viewToBasePosition(stop + movingDeltaXRef.current);
    const selectionRangeBp = props.createRangeOnContig(previousSelectionRangeRef.current.start, newRangeStop);
    nextSelectionRangeRef.current = selectionRangeBp;
    setCurrentSelectionRange(selectionRangeBp);
  };

  useEffect(() => {
    // Sync the local selection range with the parent (GenomeViewerInteractiveSurface)
    setCurrentSelectionRange(props.selectionRange);
  }, [props.selectionRange]);

  const debouncedUpdateSelectedRange = useDebouncedCallback((range: Range) => {
    // Set the selected range in redux
    dispatch(selectRange(range));
    // Update any selection
    dispatch(updateSelectionModelFromRange(range));
  }, 500);

  useEffect(() => {
    if (props.selectionMode !== RangeSelectionMode.Zoom) {
      debouncedUpdateSelectedRange(currentSelectionRange);
    }
  }, [currentSelectionRange, dispatch, props.selectionMode, debouncedUpdateSelectedRange]);

  // No selection is active
  if (currentSelectionRange == undefined) return null;

  const start = Math.round(normalize(currentSelectionRange.start - 1));
  const stop = Math.round(normalize(currentSelectionRange.stop));
  const selectionRectX = clamp(start, 0, layout.trackWidth);
  const selectionRectWidth = clamp(stop - selectionRectX, 0, layout.trackWidth - selectionRectX);
  const leftOverflow = start < 0 || start > layout.trackWidth;
  const rightOverflow = stop < 0 || stop > layout.trackWidth;

  return (
    <svg
      width={layout.trackWidth}
      height={layout.viewHeight + AXIS_HEIGHT + 2}
      style={{
        position: "absolute",
        width: "100%",
        top: 0,
        left: layout.headerWidth,
        pointerEvents: "none",
        overflow: "visible",
        zIndex: 3,
        userSelect: "none",
      }}
    >
      <rect
        className={props.selectionMode === RangeSelectionMode.Zoom ? "slider-selection" : "range-selection"}
        x={selectionRectX + 0.5}
        y={-0.5}
        width={selectionRectWidth}
        height={layout.viewHeight + AXIS_HEIGHT + 2}
      />
      {props.selectionMode !== RangeSelectionMode.Zoom && (
        <>
          <RectHandle
            x={selectionRectX - RESIZE_HANDLE_WIDTH / 2 + 0.5}
            visibility={leftOverflow ? "hidden" : "visible"}
            onMoveStart={onRectHandleMoveStart}
            onMove={onLeftRectHandleMove}
            onMoveEnd={onRectHandleMoveEnd}
          />
          <RectHandle
            x={selectionRectX + selectionRectWidth - RESIZE_HANDLE_WIDTH / 2 + 0.5}
            visibility={rightOverflow ? "hidden" : "visible"}
            onMoveStart={onRectHandleMoveStart}
            onMove={onRightRectHandleMove}
            onMoveEnd={onRectHandleMoveEnd}
          />
          {selectionRectWidth > MIN_SELECTION_LABEL_WIDTH && (
            <text
              className={"range-selection-label"}
              x={selectionRectX + selectionRectWidth / 2 + 0.5}
              y={SELECTION_LABEL_YPOS}
              textAnchor={"middle"}
            >
              {formatNumberByThousands(getRangeLength(currentSelectionRange)) + " bp"}
            </text>
          )}
          <text
            className={"range-selection-label"}
            x={selectionRectX + 0.5}
            y={SELECTION_LABEL_YPOS}
            visibility={leftOverflow ? "hidden" : "visible"}
            textAnchor={"middle"}
          >
            {formatNumberByThousands(currentSelectionRange.start)}
          </text>
          <text
            className={"range-selection-label"}
            x={selectionRectX + selectionRectWidth + 0.5}
            y={SELECTION_LABEL_YPOS}
            visibility={rightOverflow ? "hidden" : "visible"}
            textAnchor={"middle"}
          >
            {formatNumberByThousands(currentSelectionRange.stop)}
          </text>
        </>
      )}
    </svg>
  );
};

interface RectHandleProps extends MoveEvents {
  x: number;
  visibility: "hidden" | "visible";
}

const RectHandle: FC<RectHandleProps> = (props) => {
  const { moveProps } = useMove(props);

  return (
    <rect
      {...moveProps}
      className={"range-resize-handle"}
      x={props.x}
      y={1.5}
      width={RESIZE_HANDLE_WIDTH}
      height={AXIS_HEIGHT - 3}
      visibility={props.visibility}
    />
  );
};

export { GenomeViewerSelectionRectangle };
